import React, { useEffect, useMemo, useState } from 'react';
import { withCreds } from '../authService';
import { CelestialShell, PageHeader, SystemFooter } from './ui/CelestialShell';

function labelFor(key) {
  return String(key || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function valueFor(value) {
  if (value == null) return 'Not available yet';
  if (typeof value === 'boolean') return value ? 'Active' : 'Quiet';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map((entry) => typeof entry === 'object' ? valueFor(entry) : entry).join(' · ');
  return Object.entries(value).slice(0, 4).map(([key, entry]) => `${labelFor(key)}: ${valueFor(entry)}`).join(' · ');
}

function insightTitle(item, index) {
  if (typeof item === 'string') return `Reflection ${String(index + 1).padStart(2, '0')}`;
  return item?.title || item?.name || item?.label || item?.category || `Reflection ${String(index + 1).padStart(2, '0')}`;
}

function insightBody(item) {
  if (typeof item === 'string') return item;
  return item?.summary || item?.description || item?.insight || item?.text || valueFor(item);
}

export default function InsightsDashboard() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError('');
      const tryGetJson = async (path) => {
        try {
          const response = await withCreds(path, { method: 'GET', headers: { 'Cache-Control': 'no-store' } });
          if (!response?.ok) return null;
          return await response.json().catch(() => ({}));
        } catch (_) {
          return null;
        }
      };

      const nextOverview = (await tryGetJson('/insights/overview')) || (await tryGetJson('/api/insights/overview')) || null;
      const list = (await tryGetJson('/insights')) || (await tryGetJson('/insights/list')) || (await tryGetJson('/api/insights')) || null;
      if (!alive) return;

      const nextItems = (Array.isArray(list?.items) && list.items) || (Array.isArray(list?.insights) && list.insights) || (Array.isArray(list) && list) || [];
      setOverview(nextOverview);
      setItems(nextItems);
      setLoading(false);
      if (!nextOverview && nextItems.length === 0) setError('No reflections have formed yet. Insights will remain quiet until there is something genuinely useful to show.');
    }
    load();
    return () => { alive = false; };
  }, []);

  const overviewEntries = useMemo(() => overview && typeof overview === 'object' ? Object.entries(overview).slice(0, 6) : [], [overview]);

  return (
    <CelestialShell authenticated>
      <main className="br-container">
        <PageHeader eyebrow="Patterns, by invitation" title="Insights" description="A considered view of recurring themes, not a judgment, score, or diagnosis. These reflections are here only to help you notice what may be worth your attention." />

        {loading && <section className="br-panel br-glass br-system-state"><div><div className="br-system-state__mark" aria-hidden="true" /><h1>Gathering the signal</h1><p>Looking for patterns carefully, without forcing meaning where there is none.</p></div></section>}

        {!loading && error && <section className="br-panel br-glass br-system-state"><div><div className="br-system-state__mark" aria-hidden="true" /><h1>Quiet for now</h1><p>{error}</p></div></section>}

        {!loading && overviewEntries.length > 0 && (
          <section className="br-panel br-glass br-section">
            <div className="br-section__header"><p className="br-eyebrow">Overview</p><h2 className="br-section__title">The shape of recent reflection</h2></div>
            <div className="br-grid br-grid--3">
              {overviewEntries.map(([key, value]) => (
                <article className="br-card" key={key}><div className="br-card__inner"><p className="br-card__label">{labelFor(key)}</p><p className="br-card__value">{valueFor(value)}</p></div></article>
              ))}
            </div>
          </section>
        )}

        {!loading && items.length > 0 && (
          <section className="br-panel br-glass br-section">
            <div className="br-section__header"><p className="br-eyebrow">Reflections · {items.length}</p><h2 className="br-section__title">What may deserve another look</h2><p className="br-section__copy">Each observation remains separate and can be revisited without becoming a label.</p></div>
            <div className="br-grid br-grid--2">
              {items.map((item, index) => (
                <article className="br-card" key={item?.id || index}><div className="br-card__inner"><p className="br-card__label">{String(index + 1).padStart(2, '0')}</p><h3 className="br-card__title">{insightTitle(item, index)}</h3><p className="br-card__copy">{insightBody(item)}</p></div></article>
              ))}
            </div>
          </section>
        )}
        <SystemFooter />
      </main>
    </CelestialShell>
  );
}
